/**
 * Provider Invitation Service — formal provider-invitation lifecycle
 * (ADR-131 D3/D4), including the ad-hoc (single-alert, time-boxed) variant.
 *
 * Endpoints:
 *   - GET  /api/v2/provider-invitation/        — paginated collection list.
 *   - POST /api/v2/provider-invitation/adhoc/  — create an alert-bound
 *     ad-hoc invitation. Accepts only `alert` and `provider_org`; unlike the
 *     collection list, this action returns a single invitation object.
 *   - POST /api/v2/provider-invitation/{id}/revoke/   — revoke an invitation.
 *
 * There is deliberately NO reinvite endpoint: re-inviting a revoked org goes
 * through the ordinary /adhoc/ create, which re-opens the revoked row in
 * place.
 */
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
    if (result.error !== undefined) {
        throw result.error;
    }
    return result.data as T;
}

export interface AdhocProviderInvitationPayload {
    alert: number;
    provider_org: number;
}

/** POST /api/v2/provider-invitation/adhoc/ response — a single invitation. */
export interface AdhocProviderInvitationResponse {
    id: number;
    status: string;
    alert: number | null;
    provider_org: number;
    expires_at: string | null;
}

/** A row of the GET /api/v2/provider-invitation/ collection list. */
export interface ProviderInvitation {
    id: number;
    status: string;
    alert: number | null;
    provider_org: number;
    provider_org_name: string;
    expires_at: string | null;
}

/** GET /api/v2/provider-invitation/ response — paginated, never a bare object. */
export interface PaginatedProviderInvitationList {
    count: number;
    next?: string | null;
    previous?: string | null;
    results: ProviderInvitation[];
}

export interface GetAdhocProviderInvitationParams {
    alert?: number;
    provider_org?: number;
}

/**
 * GET /api/v2/provider-invitation/
 */
export async function getAdhocProviderInvitation(
    params?: GetAdhocProviderInvitationParams,
): Promise<PaginatedProviderInvitationList> {
    const result = await (client.GET as any)('/api/v2/provider-invitation/', {
        params: {
            query: {
                alert: params?.alert,
                provider_org: params?.provider_org,
            },
        },
    });
    return unwrap(result) as unknown as PaginatedProviderInvitationList;
}

/**
 * POST /api/v2/provider-invitation/adhoc/
 */
export async function createAdhocProviderInvitation(
    payload: AdhocProviderInvitationPayload,
): Promise<AdhocProviderInvitationResponse> {
    const result = await (client.POST as any)('/api/v2/provider-invitation/adhoc/', {
        body: payload,
    });
    return unwrap(result) as unknown as AdhocProviderInvitationResponse;
}

/**
 * POST /api/v2/provider-invitation/{id}/revoke/
 */
export async function deleteAdhocProviderInvitation(
    id: string | number,
): Promise<AdhocProviderInvitationResponse> {
    const result = await (client.POST as any)(
        `/api/v2/provider-invitation/${id}/revoke/`,
        {},
    );
    return unwrap(result) as unknown as AdhocProviderInvitationResponse;
}
